import type {
  CallbackRegister,
  RegisteredPluginHandlers,
} from './flotiqPluginsRegistry';
import type FlotiqPluginsRegistry from './flotiqPluginsRegistry';
import type { FlotiqPluginCallback, FlotiqPluginInfo } from './types';

/**
 * This handler is passed to each plugin instance to allow it to register callbacks for events or run new events.
 *
 * @memberof FlotiqPlugins.Core
 */
class PluginEventHandler {
  /** Plugin info. */
  plugin: FlotiqPluginInfo;

  /** Plugin registry. */
  registry: typeof FlotiqPluginsRegistry;

  #callbackRegister: CallbackRegister;

  readonly #registeredPluginHandlers: RegisteredPluginHandlers;

  constructor(
    pluginInfo: FlotiqPluginInfo,
    registry: typeof FlotiqPluginsRegistry,
    callbackRegister: CallbackRegister,
    registeredPluginHandlers: RegisteredPluginHandlers,
  ) {
    this.plugin = pluginInfo;
    this.registry = registry;
    this.#callbackRegister = callbackRegister;
    this.#registeredPluginHandlers = registeredPluginHandlers;
  }

  /** Registers a callback for given event. */
  on(event: string, callback: FlotiqPluginCallback) {
    if (!this.#callbackRegister[event]) this.#callbackRegister[event] = {};
    if (!this.#callbackRegister[event][this.plugin.id])
      this.#callbackRegister[event][this.plugin.id] = [callback];
    else this.#callbackRegister[event][this.plugin.id].push(callback);
  }

  /** Runs an event with given params. */
  run(event: string, ...params: Parameters<FlotiqPluginCallback>) {
    this.registry.run(event, ...params);
  }

  /** Unregisters all callbacks for given plugin. */
  unregister() {
    Object.keys(this.#callbackRegister).forEach((event) => {
      if (this.#callbackRegister[event][this.plugin.id]) {
        delete this.#callbackRegister[event][this.plugin.id];
      }
    });
    delete this.#registeredPluginHandlers[this.plugin.id];
  }
}

export { PluginEventHandler };
